// components/account/AccountHistoricsModal/components/StatisticsPanel.jsx
import React from 'react';
import { Badge } from 'react-bootstrap';
import { FaChartBar, FaFile, FaMoneyBillWave, FaExchangeAlt } from 'react-icons/fa';
import { formatAmount } from '../../service/utils/transactionUtils';

/**
 * Panel de statistiques - Affiche les principales métriques
 */
const StatisticsPanel = ({ data }) => {
  if (!data) return null;

  const stats = {
    totalHistorics: data.totalHistorics || 0,
    totalDocuments: data.totalDocumentsWithPreuve || 0,
    totalAmount: data.summary?.totalAmount || 0,
    containerStats: data.containerStatistics || {}
  };

  /**
   * Calcule le pourcentage d'historiques avec documents
   */
  const getDocumentPercentage = () => {
    if (stats.totalHistorics === 0) return 0;
    return Math.round((stats.totalDocuments / stats.totalHistorics) * 100);
  };

  /**
   * Calcule le total par type de container
   */
  const getContainerStats = () => {
    if (!stats.containerStats.foundInDocuments && !stats.containerStats.foundInTransactionPreuves) {
      return null;
    }
    
    return {
      cash: stats.containerStats.foundInDocuments || 0,
      transactions: stats.containerStats.foundInTransactionPreuves || 0,
      successRate: stats.containerStats.successRate || 0
    };
  };

  const containerStats = getContainerStats();
  const documentPercentage = getDocumentPercentage();

  return (
    <div className="row mb-4">
      {/* Statistique: Total des historiques */}
      <div className="col-md-3 mb-3">
        <div className="card h-100 bg-primary bg-opacity-10 border-primary">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h2 className="text-white mb-1">{stats.totalHistorics}</h2>
                <p className="text-white mb-0">Historiques</p>
              </div>
              <div className="bg-primary bg-opacity-25 p-3 rounded-circle text-white">
                <FaChartBar className="text-white" size={24} />
              </div>
            </div>
            <div className="mt-3">
              <div className="progress" style={{ height: '6px' }}>
                <div 
                  className="progress-bar bg-primary" 
                  role="progressbar" 
                  style={{ width: '100%' }}
                  aria-valuenow={100}
                  aria-valuemin="0"
                  aria-valuemax="100"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Statistique: Documents avec preuve */}
      <div className="col-md-3 mb-3">
        <div className="card h-100 bg-success bg-opacity-10 border-success">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h2 className="text-success mb-1">{stats.totalDocuments}</h2>
                <p className="text-muted mb-0">Avec documents</p>
              </div>
              <div className="bg-success bg-opacity-25 p-3 rounded-circle">
                <FaFile className="text-success" size={24} />
              </div>
            </div>
            <div className="mt-3">
              <div className="d-flex justify-content-between mb-1">
                <small className="text-muted">Couverture</small>
                <small className="fw-bold text-success">{documentPercentage}%</small>
              </div>
              <div className="progress" style={{ height: '6px' }}>
                <div 
                  className="progress-bar bg-success" 
                  role="progressbar" 
                  style={{ width: `${documentPercentage}%` }}
                  aria-valuenow={documentPercentage}
                  aria-valuemin="0"
                  aria-valuemax="100"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Statistique: Montant total */}
      <div className="col-md-3 mb-3">
        <div className="card h-100 bg-info bg-opacity-10 border-info">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h5 className="text-info mb-1">
                  {formatAmount(stats.totalAmount)}
                </h5>
                <p className="text-muted mb-0">Montant total</p>
              </div>
              <div className="bg-info bg-opacity-25 p-3 rounded-circle">
                <FaMoneyBillWave className="text-info" size={24} />
              </div>
            </div>
            <div className="mt-3">
              <Badge bg="light" text="dark" className="me-2">
                <small>Toutes catégories</small>
              </Badge>
            </div>
          </div>
        </div>
      </div>

      {/* Statistique: Répartition des documents */}
      <div className="col-md-3 mb-3">
        <div className="card h-100 bg-warning bg-opacity-10 border-warning">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h5 className="text-warning mb-1">Répartition</h5>
                <p className="text-muted mb-0">Type de documents</p>
              </div>
              <div className="bg-warning bg-opacity-25 p-3 rounded-circle">
                <FaExchangeAlt className="text-warning" size={24} />
              </div>
            </div>
            <div className="mt-3">
              {containerStats ? (
                <>
                  <div className="d-flex justify-content-between mb-1">
                    <small>
                      <Badge bg="bg-primary bg-opacity-10 border-primary text-primary" className="me-1">Cash</Badge>
                      <span className="text-muted">{containerStats.cash}</span>
                    </small>
                    <small>
                      <Badge bg="info bg-opacity-10 border-info text-info" className="me-1">Transaction</Badge>
                      <span className="text-muted">{containerStats.transactions}</span>
                    </small>
                  </div>
                  <div className="progress" style={{ height: '6px' }}>
                    <div 
                      className="progress-bar bg-primary" 
                      style={{ 
                        width: `${(containerStats.cash / (containerStats.cash + containerStats.transactions)) * 100}%` 
                      }}
                    />
                    <div 
                      className="progress-bar bg-info" 
                      style={{ 
                        width: `${(containerStats.transactions / (containerStats.cash + containerStats.transactions)) * 100}%` 
                      }}
                    />
                  </div>
                  <small className="text-muted mt-2 d-block">
                    Taux de succès: <strong>{containerStats.successRate}%</strong>
                  </small>
                </>
              ) : (
                <small className="text-muted">Aucune donnée disponible</small>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatisticsPanel;